/**
 * Header Component
 * Top navigation bar with user menu and theme switcher
 * Shows role-based profile information
 */

import { useMemo } from 'react';
import { Layout, Dropdown, Avatar, Typography } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  ProfileOutlined,
  DashboardOutlined,
  DownOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { PATHS } from '../../../routes/constants/paths';
import { getUserRole, isAdmin, isDoctor, isReceptionist, getDefaultDashboardPath } from '../../../routes/utils/auth';
import CareRequestMessageNotificationsBell from './CareRequestMessageNotificationsBell';
import './Header.scss';

const { Header: AntHeader } = Layout;
const { Text } = Typography;

/**
 * Get user from localStorage
 */
const getUser = () => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch (e) {
      return null;
    }
  }
  return null;
};

/**
 * Role label when no agency context (secondary line in identity chip)
 */
const getRoleLabel = (role: string | null): string => {
  if (role === 'RECEPTIONIST') return 'Receptionist';
  if (role === 'AGENCY_ADMIN' || role === 'ADMIN' || role === 'SUPER_ADMIN') return 'Admin';
  if (role === 'CAREGIVER' || role === 'NURSE' || role === 'STAFF') return 'Staff';
  return 'User';
};

/**
 * Get page title based on current route
 */
const getPageTitle = (pathname: string): string => {
  if (pathname.includes('/dashboard')) return 'Dashboard';
  if (pathname.includes('/service-requests')) return 'Care Requests';
  if (pathname.includes('/tickets')) return 'Help & Support';
  if (pathname.includes('/patients')) return 'Patients';
  if (pathname.includes('/referrals')) return 'Referrals';
  if (pathname.includes('/reviews')) return 'Patient Reviews';
  if (pathname.includes('/staffing')) return 'Staffing';
  if (pathname.includes('/caregivers') || pathname.includes('/caregiver')) return 'Staff';
  if (pathname.includes('/appointments')) return 'Appointments';
  if (pathname.includes('/schedule')) return 'Schedule';
  if (pathname.includes('/messages')) return 'Messages';
  if (pathname.includes('/payments')) return 'Payments';
  if (pathname.includes('/analytics')) return 'Analytics';
  if (pathname.includes('/agency')) return 'Agency Setup';
  if (pathname.includes('/receptionist')) return 'Receptionist Portal';
  if (pathname.includes('/doctor')) return 'Doctor Portal';
  return 'Dashboard';
};

/**
 * Header Component
 */
export const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useMemo(() => getUser(), []);
  const userRole = getUserRole();

  const pageTitle = useMemo(() => getPageTitle(location.pathname), [location.pathname]);
  const agencyName = useMemo(() => {
    if (!user) return '';
    return (
      user.agency?.agencyInfo?.agencyName ||
      user.agency?.agencyInfo?.legalBusinessName ||
      user.agency?.legalBusinessName ||
      user.agency?.agencyName ||
      user.agencyName ||
      user.companyName ||
      ''
    );
  }, [user]);

  const handleLogout = () => {
    // Clear all local storage data
    localStorage.clear();
    
    // Navigate to login
    navigate('/login');
  };

  // Get role-specific menu items
  const getUserMenuItems = (): MenuProps['items'] => {
    const items: MenuProps['items'] = [];

    // Profile link based on role
    if (isReceptionist()) {
      items.push({
        key: 'receptionist-profile',
        icon: <ProfileOutlined />,
        label: 'Receptionist Profile',
        onClick: () => {
          navigate(`${PATHS.RECEPTIONIST_SETTINGS}/profile`);
        },
      });
    } else if (isAdmin()) {
      items.push({
        key: 'agency-profile',
        icon: <ProfileOutlined />,
        label: 'Agency Setup',
        onClick: () => navigate(PATHS.AGENCY_ONBOARDING_STEP(1)),
      });
    } else if (isDoctor()) {
      items.push({
        key: 'doctor-profile',
        icon: <ProfileOutlined />,
        label: 'Doctor Profile',
        onClick: () => navigate(PATHS.DOCTOR_PROFILE),
      });
    }

    // Dashboard link based on role and profile status
    items.push({
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      onClick: () => {
        const defaultPath = getDefaultDashboardPath();
        navigate(defaultPath);
      },
    });

    items.push({ type: 'divider' });

    items.push({
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
      danger: true,
    });

    return items;
  };

  /**
   * Get user avatar based on role
   */
  const getUserAvatar = (): string | undefined => {
    if (!user) return undefined;

    // Prefer backend-provided avatarUrl (directly viewable)
    if (user.avatarUrl) {
      return user.avatarUrl;
    }

    // First, check if avatar is directly on user object
    if (user.avatar) {
      return user.avatar;
    }

    // For admin, check agency profile
    if (isAdmin()) {
      const agencyProfile = localStorage.getItem('agencyProfile');
      if (agencyProfile) {
        try {
          const profile = JSON.parse(agencyProfile);
          if (profile.logo) return profile.logo;
        } catch (e) {
          console.error('Error parsing agency profile:', e);
        }
      }
    }

    // For doctor, check doctor profile
    if (isDoctor()) {
      const doctorProfile = localStorage.getItem('doctorProfile');
      if (doctorProfile) {
        try {
          const profile = JSON.parse(doctorProfile);
          if (profile.profilePicture) return profile.profilePicture;
        } catch (e) {
          console.error('Error parsing doctor profile:', e);
        }
      }
    }

    return undefined;
  };

  const userAvatar = getUserAvatar();
  const menuItems = getUserMenuItems();

  const displayName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.name || user?.email || 'User';

  /** One container: HH/agency name (primary) + signed-in name (secondary), clinic-style */
  const primaryTitle = agencyName || displayName;
  const secondarySubtitle = agencyName ? displayName : getRoleLabel(userRole);

  return (
    <AntHeader className="app-header">
      <div className="app-header__left">
        <div className="app-header__branding">
          <Text className="app-header__page-title">{pageTitle}</Text>
        </div>
      </div>

      <div className="app-header__right">
        <CareRequestMessageNotificationsBell />
        <Dropdown
          menu={{ items: menuItems }}
          trigger={['click']}
          placement="bottomRight"
          overlayClassName="app-header__dropdown"
        >
          <div className="app-header__identity" role="button" tabIndex={0}>
            <Avatar
              size={44}
              icon={<UserOutlined />}
              src={userAvatar}
              className="app-header__identity-avatar"
              style={{
                backgroundColor: userAvatar ? undefined : 'var(--color-primary, #2563eb)',
                color: '#fff',
              }}
            />
            <div className="app-header__identity-text">
              <Text
                className={
                  agencyName
                    ? 'app-header__identity-primary'
                    : 'app-header__identity-primary app-header__identity-primary--contact'
                }
              >
                {primaryTitle}
              </Text>
              <Text
                className={
                  agencyName
                    ? 'app-header__identity-secondary app-header__identity-secondary--contact'
                    : 'app-header__identity-secondary'
                }
              >
                {secondarySubtitle}
              </Text>
            </div>
            <DownOutlined className="app-header__identity-chevron" />
          </div>
        </Dropdown>
      </div>
    </AntHeader>
  );
};

export default Header;
